import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Search, ListFilter, FileDown, RotateCcw, X, Download, FileText, FileSpreadsheet } from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import agentRollbackService from "../../services/agentRollbackService.js";
import { handleSetSearch } from "../../utils/searchDebouncing.js";
import ARHCustomDataTable from "../../components/dataTable/ARHCustomDataTable.jsx";
import { useTranslation } from "react-i18next";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

function AgentRollbackReports() {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.auth.user);
  const searchRef = React.useRef(null);
  const exportMenuRef = useRef(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isDark, setIsDark] = useState(false);
  
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);
  
  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    status: "",
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["agentRollbacks", filter],
    queryFn: () => agentRollbackService.getAgentRollbacks(
      filter.page, 
      filter.limit, 
      filter.search,
      null, 
      null,  
      filter.status
    ),
  });

  const rollbackData = useMemo(() => {
    if (!data) {
      console.log("No data received");
      return [];
    }
    
    console.log("=== FULL API RESPONSE ===");
    console.log("data:", data);
    console.log("data.data:", data.data);
    console.log("data.data type:", typeof data.data);
    console.log("data.data keys:", data.data ? Object.keys(data.data) : "No keys");
    

    let actualData = [];
    

    const possibleArrayProperties = ['data', 'rollbacks', 'records', 'items', 'list', 'results'];
    
    for (const prop of possibleArrayProperties) {
      if (data.data && data.data[prop] && Array.isArray(data.data[prop])) {
        console.log(`Found array in data.data.${prop}`);
        actualData = data.data[prop];
        break;
      }
    }
    

    if (actualData.length === 0) {
      if (data.data && typeof data.data === 'object' && data.data.id) {
        console.log("data.data is a single rollback object:", data.data);
        actualData = [data.data];
      } else if (Array.isArray(data.data)) {
        console.log("data.data is already an array");
        actualData = data.data;
      } else if (Array.isArray(data)) {
        console.log("data itself is an array");
        actualData = data;
      }
    }
    
    console.log("Final actualData:", actualData);
    console.log("Final actualData length:", actualData.length);
    console.log("First item:", actualData[0]);
    console.log("=== END DEBUG ===");
    
    return actualData;
  }, [data]);

  const metaData = data?.meta || {};
  

  const totalRollbacks = metaData.total || rollbackData.length;
  const pendingCount = rollbackData.filter(item => item?.status === 'pending').length;
  const reversedCount = rollbackData.filter(item => item?.status === 'reversed').length;
  const rejectedCount = rollbackData.filter(item => item?.status === 'rejected').length;


  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = (value) => {
    setFilter((prev) => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setFilter((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilter((prev) => ({ 
      ...prev, 
      status: status === prev.status ? "" : status,
      page: 1 
    }));
  };

  const clearFilters = () => {
    setFilter({
      search: "",
      limit: 10,
      page: 1,
      status: "",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": 
        return isDark ? "bg-yellow-900/30 text-yellow-300" : "bg-yellow-100 text-yellow-800";
      case "reversed": 
        return isDark ? "bg-green-900/30 text-green-300" : "bg-green-100 text-green-800";
      case "rejected": 
        return isDark ? "bg-red-900/30 text-red-300" : "bg-red-100 text-red-800";
      default: 
        return isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-800";
    }
  };

  // Map operator values
  const getOperatorName = (operatorCode) => {
    const operatorMap = {
      "1": "AWCC",
      "2": "Salaam",
      "3": "Roshan", 
      "4": "MTN",
      "5": "Etisalat"
    };
    return operatorMap[operatorCode] || operatorCode || "";
  };


  const formatStatus = (status) => {
    if (!status) return "";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };


  const prepareExportData = (dataArray) => {
    return dataArray.map(item => ({
      [t('rollbackReports.export.rollbackId')]: item?.id || "",
      [t('rollbackReports.export.transactionNumber')]: item?.txnNumber || "",
      [t('rollbackReports.export.operator')]: getOperatorName(item?.operator),
      [t('rollbackReports.export.originalAmount')]: item?.rechargeDetails?.amount || 0,
      [t('rollbackReports.export.reversedAmount')]: item?.reversedAmount && parseFloat(item.reversedAmount) > 0 ? item.reversedAmount : 0,
      [t('rollbackReports.export.status')]: t(`rollbackReports.status.${item?.status || 'unknown'}`),
      [t('rollbackReports.export.rejectionReason')]: item?.rejectionReason || "",
      [t('rollbackReports.export.reversingNote')]: item?.reversingNote || "",
      [t('rollbackReports.export.requestedDate')]: item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "",
      [t('rollbackReports.export.requestedTime')]: item?.createdAt ? new Date(item.createdAt).toLocaleTimeString() : "",
      [t('rollbackReports.export.createdAt')]: item?.createdAt ? new Date(item.createdAt).toISOString() : "",
      [t('rollbackReports.export.updatedAt')]: item?.updatedAt ? new Date(item.updatedAt).toISOString() : ""
    }));
  };


  const exportToPDF = async (exportAll = false) => {
    try {
      setShowExportMenu(false);
      
      let dataToExport = rollbackData;
      
      if (exportAll) {
        const exportBtn = document.querySelector('.export-btn');
        if (exportBtn) {
          exportBtn.disabled = true;
          exportBtn.innerHTML = `<span class="animate-spin mr-2">⏳</span> ${t('rollbackReports.export.preparingPDF')}`;
        }

        const allData = await agentRollbackService.getAgentRollbacks(
          1,
          1000,
          filter.search,
          null,
          null,
          filter.status
        );

        let actualData = [];
        const possibleArrayProperties = ['data', 'rollbacks', 'records', 'items', 'list', 'results'];
        
        for (const prop of possibleArrayProperties) {
          if (allData.data && allData.data[prop] && Array.isArray(allData.data[prop])) {
            actualData = allData.data[prop];
            break;
          }
        }

        if (actualData.length === 0 && allData.data && typeof allData.data === 'object') {
          actualData = [allData.data];
        }

        dataToExport = actualData;
      }

      if (dataToExport.length === 0) {
        alert(t('rollbackReports.export.noData'));
        return;
      }

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      
      doc.setFontSize(16);
      doc.setTextColor(40);
      doc.text(t('rollbackReports.title'), pageWidth / 2, 15, { align: "center" });
      
      doc.setFontSize(10);
      doc.setTextColor(100);
      
      const filterTexts = [];
      if (filter.search) filterTexts.push(`${t('common.search')}: ${filter.search}`);
      if (filter.status) filterTexts.push(`${t('common.status')}: ${t(`rollbackReports.status.${filter.status}`)}`);
      
      const filterInfo = filterTexts.length > 0 
        ? `${t('common.filters')}: ${filterTexts.join(", ")}`
        : t('common.allRecords');
      
      doc.text(filterInfo, pageWidth / 2, 22, { align: "center" });
      
      doc.setFontSize(9);
      doc.setTextColor(150);
      const exportType = exportAll ? t('common.allData') : t('common.currentPage');
      const exportDate = new Date().toLocaleString();
      doc.text(`${t('common.exported')}: ${exportDate} | ${exportType}`, pageWidth / 2, 28, { align: "center" });
      
      const pending = dataToExport.filter(item => item?.status === 'pending').length;
      const reversed = dataToExport.filter(item => item?.status === 'reversed').length;
      const rejected = dataToExport.filter(item => item?.status === 'rejected').length;
      
      doc.setFontSize(10);
      doc.setTextColor(40);
      doc.text(`${t('common.total')}: ${dataToExport.length} | ${t('rollbackReports.status.pending')}: ${pending} | ${t('rollbackReports.status.reversed')}: ${reversed} | ${t('rollbackReports.status.rejected')}: ${rejected}`, 
                pageWidth / 2, 35, { align: "center" });
      
      const tableData = dataToExport.map(item => [
        item?.id || "",
        item?.txnNumber || "",
        getOperatorName(item?.operator),
        `${item?.rechargeDetails?.amount || 0} AFN`,
        item?.reversedAmount && parseFloat(item.reversedAmount) > 0 ? `${item.reversedAmount} AFN` : "0 AFN",
        t(`rollbackReports.status.${item?.status || 'unknown'}`),
        item?.rejectionReason?.substring(0, 30) + (item?.rejectionReason?.length > 30 ? "..." : "") || "",
        item?.reversingNote?.substring(0, 30) + (item?.reversingNote?.length > 30 ? "..." : "") || "",
        item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""
      ]);
      
      doc.autoTable({
        head: [[
          t('rollbackReports.columns.id'),
          t('rollbackReports.columns.txnNumber'),
          t('rollbackReports.columns.operator'),
          t('rollbackReports.columns.amount'),
          t('rollbackReports.columns.reversedAmount'),
          t('rollbackReports.columns.status'),
          t('rollbackReports.columns.rejectionReason'),
          t('rollbackReports.columns.reversingNote'),
          t('rollbackReports.columns.date')
        ]],
        body: tableData,
        startY: 40,
        theme: 'grid',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 25 },
          2: { cellWidth: 20 },
          3: { cellWidth: 20 },
          4: { cellWidth: 20 },
          5: { cellWidth: 15 },
          6: { cellWidth: 30 },
          7: { cellWidth: 30 },
          8: { cellWidth: 20 }
        }
      });
      
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`${t('common.generatedBy')}: ${userInfo?.username || t('common.system')}`, 14, finalY);
      doc.text(`${t('common.page')} 1 ${t('common.of')} 1`, pageWidth - 20, finalY, { align: "right" });
      
      const fileName = `rollback_reports_${exportAll ? "all" : "page"}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert(t('rollbackReports.export.pdfFailed'));
    } finally {
      const exportBtn = document.querySelector('.export-btn');
      if (exportBtn) {
        exportBtn.disabled = false;
        exportBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> ${t('common.export')}`;
      }
    }
  };

  const exportToExcel = async (exportAll = false) => {
    try {
      setShowExportMenu(false);
      
      let dataToExport = rollbackData;
      
      if (exportAll) {
        const exportBtn = document.querySelector('.export-btn');
        if (exportBtn) {
          exportBtn.disabled = true;
          exportBtn.innerHTML = `<span class="animate-spin mr-2">⏳</span> ${t('rollbackReports.export.preparingExcel')}`;
        }

        const allData = await agentRollbackService.getAgentRollbacks(
          1,
          1000,
          filter.search,
          null,
          null,
          filter.status
        );

        let actualData = [];
        const possibleArrayProperties = ['data', 'rollbacks', 'records', 'items', 'list', 'results'];
        
        for (const prop of possibleArrayProperties) {
          if (allData.data && allData.data[prop] && Array.isArray(allData.data[prop])) {
            actualData = allData.data[prop];
            break;
          }
        }

        if (actualData.length === 0 && allData.data && typeof allData.data === 'object') {
          actualData = [allData.data];
        }

        dataToExport = actualData;
      }

      if (dataToExport.length === 0) {
        alert(t('rollbackReports.export.noData'));
        return;
      }

      const exportData = prepareExportData(dataToExport);
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      XLSX.utils.book_append_sheet(wb, ws, t('rollbackReports.title'));
      
      const summaryData = [
        [t('rollbackReports.title')],
        [t('common.generatedOn'), new Date().toLocaleString()],
        [t('common.generatedBy'), userInfo?.username || t('common.system')],
        [t('common.filters'), filter.search ? `${t('common.search')}: ${filter.search}` : t('common.none')],
        [t('common.statusFilter'), filter.status ? t(`rollbackReports.status.${filter.status}`) : t('common.all')],
        [],
        [t('common.statistics')],
        [t('common.totalRecords'), dataToExport.length],
        [t('rollbackReports.status.pending'), dataToExport.filter(item => item?.status === 'pending').length],
        [t('rollbackReports.status.reversed'), dataToExport.filter(item => item?.status === 'reversed').length],
        [t('rollbackReports.status.rejected'), dataToExport.filter(item => item?.status === 'rejected').length],
        [],
        [t('common.exportInformation')],
        [t('common.exportType'), exportAll ? t('common.allData') : t('common.currentPage')],
        [t('common.dataRange'), exportAll ? t('common.allFilteredRecords') : t('common.currentPageOnly')]
      ];
      
      const ws2 = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, ws2, t('common.summary'));
      

      const wscols = [
        { wch: 15 }, 
        { wch: 20 },
        { wch: 10 }, 
        { wch: 15 }, 
        { wch: 15 },
        { wch: 12 }, 
        { wch: 25 },
        { wch: 25 }, 
        { wch: 12 }, 
        { wch: 12 }, 
        { wch: 20 }, 
        { wch: 20 }  
      ];
      ws['!cols'] = wscols;
      
      const fileName = `rollback_reports_${exportAll ? "all" : "page"}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error("Error exporting Excel:", error);
      alert(t('rollbackReports.export.excelFailed'));
    } finally {
      const exportBtn = document.querySelector('.export-btn');
      if (exportBtn) {
        exportBtn.disabled = false;
        exportBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> ${t('common.export')}`;
      }
    }
  };

  const exportToCSV = async (exportAll = false) => {
    try {
      setShowExportMenu(false);
      
      let dataToExport = rollbackData;
      
      if (exportAll) {
        const exportBtn = document.querySelector('.export-btn');
        if (exportBtn) {
          exportBtn.disabled = true;
          exportBtn.innerHTML = `<span class="animate-spin mr-2">⏳</span> ${t('rollbackReports.export.preparingCSV')}`;
        }

        const allData = await agentRollbackService.getAgentRollbacks(
          1,
          1000,
          filter.search,
          null,
          null,
          filter.status
        );

        let actualData = [];
        const possibleArrayProperties = ['data', 'rollbacks', 'records', 'items', 'list', 'results'];
        
        for (const prop of possibleArrayProperties) {
          if (allData.data && allData.data[prop] && Array.isArray(allData.data[prop])) {
            actualData = allData.data[prop];
            break;
          }
        }

        if (actualData.length === 0 && allData.data && typeof allData.data === 'object') {
          actualData = [allData.data];
        }

        dataToExport = actualData;
      }

      if (dataToExport.length === 0) {
        alert(t('rollbackReports.export.noData'));
        return;
      }

      const exportData = prepareExportData(dataToExport);
      const headers = Object.keys(exportData[0]);
      const csvRows = exportData.map(row => 
        headers.map(header => {
          const cell = row[header];
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(',')
      );

      const csvContent = [
        headers.join(','),
        ...csvRows
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      link.setAttribute("href", url);
      link.setAttribute("download", `rollback_reports_${exportAll ? "all" : "page"}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert(t('rollbackReports.export.csvFailed'));
    } finally {
      const exportBtn = document.querySelector('.export-btn');
      if (exportBtn) {
        exportBtn.disabled = false;
        exportBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> ${t('common.export')}`;
      }
    }
  };

  const columns = [
    { 
      key: "id", 
      label: t('rollbackReports.columns.id')
    },
    { 
      key: "txnNumber", 
      label: t('rollbackReports.columns.txnNumber')
    },
    { 
      key: "operator", 
      label: t('rollbackReports.columns.operator'),
      render: (item) => getOperatorName(item?.operator) || "-"
    },
    {
      key: "amount",
      label: t('rollbackReports.columns.amount'),
      render: (item) => `${parseFloat(item?.rechargeDetails?.amount).toFixed(2) || 0} AFN`
    },
    {
      key: "reversedAmount",
      label: t('rollbackReports.columns.reversedAmount'), 
      render: (item) => item?.reversedAmount && parseFloat(item.reversedAmount).toFixed(2) > 0 ? `${parseFloat(item.reversedAmount).toFixed(2)} AFN` : "-"
    },
    {
      key: "status",
      label: t('rollbackReports.columns.status'),
      render: (item) => (
        <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(item?.status)}`}>
          {t(`rollbackReports.status.${item?.status || 'unknown'}`) || "-"}
        </span>
      )
    },
    {
      key: "rejectionReason",
      label: t('rollbackReports.columns.rejectionReason'),
      render: (item) => item?.rejectionReason || "-"
    },
    {
      key: "reversingNote", 
      label: t('rollbackReports.columns.reversingNote'),
      render: (item) => item?.reversingNote || "-"
    },
    {
      key: "createdAt",
      label: t('rollbackReports.columns.requestedAt'),
      render: (item) => item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"
    },
  ];

  const customEmptyState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img 
        src="/images/Search.png" 
        alt={t('common.noDataFound')}
        className="w-[170px] h-[170px]"
      />
      <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {t('rollbackReports.noRequestsFound')}
      </p>
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img 
        src="/images/Search.png" 
        alt={t('common.error')}
        className="w-[170px] h-[170px]"
      />
      <div className="text-sm text-red-600 dark:text-red-400 mb-2">
        {error?.message || t('rollbackReports.loadFailed')}
      </div>
      <button
        onClick={refetch}
        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
      >
        {t('common.retry')}
      </button>
    </div>
  );

  const hasActiveFilters = filter.search || filter.status;

  return (
    <div className={`w-full px-5 py-4 min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="w-full flex items-center justify-between gap-5 mb-6 flex-wrap">
        <div className="flex items-center justify-start flex-1 gap-5 flex-wrap">
          <div className={`max-w-[371px] w-full h-[44px] relative rounded-md border ${
            isDark 
              ? 'border-gray-600 bg-gray-800' 
              : 'border-[#E4E7EC] bg-white'
          }`}>
            <input
              ref={searchRef}
              onChange={(e) => {
                handleSetSearch(handleSearchChange, e.target.value, searchRef);
              }}
              type="text"
              placeholder={t('rollbackReports.searchPlaceholder')}
              defaultValue={filter.search}
              className={`bg-transparent h-full pl-10 transition-all duration-300 absolute left-0 top-0 px-3 py-2 w-full outline-none ${
                isDark ? 'text-white placeholder-gray-400' : ''
              }`}
            />
            <Search size={18} className={`absolute top-[12px] left-[8px] ${
              isDark ? 'text-gray-400' : 'text-gray-400'
            }`} />
            {filter.search && (
              <button
                onClick={() => handleSearchChange("")}
                className="absolute top-[12px] right-[8px] text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
              >
                ×
              </button>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            {["pending", "reversed", "rejected"].map((status) => (
              <button
                key={status}
                onClick={() => handleStatusFilter(status)}
                className={`px-3 py-2 rounded-md text-sm border transition-colors ${
                  filter.status === status
                    ? isDark
                      ? "bg-primary-900/30 text-primary-300 border-primary-700"
                      : "bg-primary-100 text-primary border-primary-300"
                    : isDark
                      ? "bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {t(`rollbackReports.status.${status}`)}
              </button>
            ))}
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className={`px-3 py-2 text-sm border rounded-md flex items-center gap-1 transition-colors ${
                isDark 
                  ? 'text-red-400 border-red-800 hover:bg-red-900/30' 
                  : 'text-red-600 border-red-300 hover:bg-red-50'
              }`}
            >
              <X size={16} />
              {t('common.clearFilters')}
            </button>
          )}
        </div>

        <div className="flex items-center justify-end">
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={rollbackData.length === 0}
              className={`export-btn px-4 py-2 rounded-md text-white text-sm flex items-center gap-2 transition-colors ${
                rollbackData.length === 0
                  ? "bg-gray-300 dark:bg-gray-600 cursor-not-allowed"
                  : "bg-primary hover:bg-[#9e0303] dark:bg-primary-700 dark:hover:bg-primary-800"
              }`}
            >
              <Download size={16} />
              {t('common.export')}
            </button>

            {showExportMenu && rollbackData.length > 0 && (
              <div className={`absolute right-0 mt-2 w-64 rounded-lg shadow-lg border z-50 ${
                isDark 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className="p-2">
                  <div className={`px-3 py-2 text-xs font-semibold border-b ${
                    isDark 
                      ? 'text-gray-400 border-gray-700' 
                      : 'text-gray-500 border-gray-200'
                  }`}>
                    {t('common.exportCurrentPage')}
                  </div>
                  <button
                    onClick={() => exportToPDF(false)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
                      isDark 
                        ? 'hover:bg-blue-900/30 text-gray-200' 
                        : 'hover:bg-blue-50 text-gray-700'
                    }`}
                  >
                    <FileText size={16} className="text-red-500 dark:text-red-400" />
                    <div>
                      <div className="font-medium">{t('common.exportAsPDF')}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('common.currentPageOnly')}
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => exportToExcel(false)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
                      isDark 
                        ? 'hover:bg-green-900/30 text-gray-200' 
                        : 'hover:bg-green-50 text-gray-700'
                    }`}
                  >
                    <FileSpreadsheet size={16} className="text-green-500 dark:text-green-400" />
                    <div>
                      <div className="font-medium">{t('common.exportAsExcel')}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('common.currentPageOnly')}
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => exportToCSV(false)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
                      isDark 
                        ? 'hover:bg-gray-700 text-gray-200' 
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <FileDown size={16} className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <div>
                      <div className="font-medium">{t('common.exportAsCSV')}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('common.currentPageOnly')}
                      </div>
                    </div>
                  </button>

                  <div className={`px-3 py-2 text-xs font-semibold border-t border-b mt-1 ${
                    isDark 
                      ? 'text-gray-400 border-gray-700' 
                      : 'text-gray-500 border-gray-200'
                  }`}>
                    {t('common.exportAllData')}
                  </div>
                  <button
                    onClick={() => exportToPDF(true)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
                      isDark 
                        ? 'hover:bg-blue-900/30 text-gray-200' 
                        : 'hover:bg-blue-50 text-gray-700'
                    }`}
                  >
                    <FileText size={16} className="text-red-500 dark:text-red-400" />
                    <div>
                      <div className="font-medium">{t('common.exportAllAsPDF')}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('common.allFilteredRecords')}
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => exportToExcel(true)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
                      isDark 
                        ? 'hover:bg-green-900/30 text-gray-200' 
                        : 'hover:bg-green-50 text-gray-700'
                    }`}
                  >
                    <FileSpreadsheet size={16} className="text-green-500 dark:text-green-400" />
                    <div>
                      <div className="font-medium">{t('common.exportAllAsExcel')}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('common.allFilteredRecords')}
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => exportToCSV(true)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
                      isDark 
                        ? 'hover:bg-gray-700 text-gray-200' 
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <FileDown size={16} className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <div>
                      <div className="font-medium">{t('common.exportAllAsCSV')}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('common.allFilteredRecords')}
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      {rollbackData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg border shadow-sm transition-colors duration-300 ${
            isDark 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('rollbackReports.stats.totalRollbacks')}
                </p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {totalRollbacks}
                </p>
              </div>
              <div className={`p-2 rounded-full ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                <RotateCcw size={20} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-lg border shadow-sm transition-colors duration-300 ${
            isDark 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('rollbackReports.status.pending')}
                </p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {pendingCount}
                </p>
              </div>
              <div className={`p-2 rounded-full ${isDark ? 'bg-yellow-900/30' : 'bg-yellow-100'}`}>
                <RotateCcw size={20} className="text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-lg border shadow-sm transition-colors duration-300 ${
            isDark 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('rollbackReports.status.reversed')}
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {reversedCount}
                </p>
              </div>
              <div className={`p-2 rounded-full ${isDark ? 'bg-green-900/30' : 'bg-green-100'}`}>
                <RotateCcw size={20} className="text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-lg border shadow-sm transition-colors duration-300 ${
            isDark 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('rollbackReports.status.rejected')}
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {rejectedCount}
                </p>
              </div>
              <div className={`p-2 rounded-full ${isDark ? 'bg-red-900/30' : 'bg-red-100'}`}>
                <RotateCcw size={20} className="text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>
        </div>
      )}


      <div className="mt-5">
        <ARHCustomDataTable
          data={rollbackData}
          columns={columns}
          meta={metaData}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={refetch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          searchQuery={filter.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
          rowClassName={`hover:bg-gray-50 cursor-pointer transition-colors ${
            isDark ? 'dark:hover:bg-gray-800' : ''
          }`}
          headerClassName={isDark ? 'bg-gray-800' : 'bg-gray-50'}
        />
      </div>
    </div>
  );
}

export default AgentRollbackReports;